

// SchoolPerformance.js - SIMPLIFIED AND WORKING VERSION
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../components/auth/AuthContext";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert,
  FormHelperText,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import axios from "axios";

const SchoolPerformance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolId: contextSchoolId } = useAuth();
  const schoolId = location.state?.schoolId || contextSchoolId;

  // Data states
  const [performanceData, setPerformanceData] = useState({});
  const [termPerformanceData, setTermPerformanceData] = useState({});
  const [loading, setLoading] = useState(false);
  const [termLoading, setTermLoading] = useState({});
  const [error, setError] = useState(null);
  const [expandedGrade, setExpandedGrade] = useState(null);
  const [expandedTerm, setExpandedTerm] = useState(null);

  // Selection states
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedExamType, setSelectedExamType] = useState("");
  const [selectedExamName, setSelectedExamName] = useState("");

  // Available options
  const [availableYears, setAvailableYears] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [availableExamTypes, setAvailableExamTypes] = useState([]);
  const [availableExamNames, setAvailableExamNames] = useState([]);
  const [fetchingExamNames, setFetchingExamNames] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Fetch available academic years on component mount
  useEffect(() => {
    if (!schoolId) {
      setError("School information not available");
      return;
    }

    const fetchAvailableYears = async () => {
      try {
        const response = await axios.get(
          `https://ed-9ny1.onrender.com/api/schools/${schoolId}/performance/years`
        );
        setAvailableYears(response.data);

        // Auto-select the most recent year
        if (response.data.length > 0) {
          setSelectedYear(response.data[0]);
        }
        setInitialLoad(false);
      } catch (err) {
        console.error("Error fetching academic years:", err);
        setError("Failed to load academic years");
        setInitialLoad(false);
      }
    };

    fetchAvailableYears();
  }, [schoolId]);

  // Fetch classes when year changes
  const fetchClasses = async (year) => {
    try {
      const response = await axios.get(
        `https://ed-9ny1.onrender.com/api/schools/${schoolId}/performance/classes`,
        { params: { academicYear: year } }
      );
      setAvailableClasses(response.data);
      setSelectedClass("");
      setSelectedTerm("");
      setSelectedExamType("");
      setSelectedExamName("");
    } catch (err) {
      console.error("Error fetching classes:", err);
      setError("Failed to load classes");
    }
  };

  // Fetch terms when class changes
  const fetchTerms = async (className, year) => {
    try {
      const response = await axios.get(
        `https://ed-9ny1.onrender.com/api/schools/${schoolId}/performance/terms`,
        { params: { class: className, academicYear: year } }
      );
      setAvailableTerms(response.data);
      setSelectedTerm("");
      setSelectedExamType("");
      setSelectedExamName("");
    } catch (err) {
      console.error("Error fetching terms:", err);
      setError("Failed to load terms");
    }
  };

  // Fetch exam types when term changes
  const fetchExamTypes = async (className, term, year) => {
    try {
      const response = await axios.get(
        `https://ed-9ny1.onrender.com/api/schools/${schoolId}/performance/exam-types`,
        { params: { class: className, term: term, academicYear: year } }
      );
      setAvailableExamTypes(response.data);
      setSelectedExamType("");
      setSelectedExamName("");
    } catch (err) {
      console.error("Error fetching exam types:", err);
      setError("Failed to load exam types");
    }
  };

  // Handle year selection
  useEffect(() => {
    if (selectedYear) {
      fetchClasses(selectedYear);
    }
  }, [selectedYear]);

  // Handle class selection
  useEffect(() => {
    if (selectedYear && selectedClass) {
      fetchTerms(selectedClass, selectedYear);
    }
  }, [selectedClass, selectedYear]);

  // Handle term selection
  useEffect(() => {
    if (selectedYear && selectedClass && selectedTerm) {
      fetchExamTypes(selectedClass, selectedTerm, selectedYear);
    }
  }, [selectedTerm, selectedYear, selectedClass]);

  // // Handle exam type selection
  

  // Handle exam type selection - UPDATED to fetch exam names for ALL exam types
  useEffect(() => {
    if (selectedYear && selectedClass && selectedTerm && selectedExamType) {
      const fetchExamNames = async () => {
        setFetchingExamNames(true);
        try {
          const response = await axios.get(
            `https://ed-9ny1.onrender.com/api/schools/${schoolId}/performance/exam-names`,
            {
              params: {
                class: selectedClass,
                term: selectedTerm,
                examType: selectedExamType,
                academicYear: selectedYear,
              },
            }
          );

          // If no exam names found, add "Unnamed" option
          let examNames = response.data;
          if (
            examNames.length === 0 &&
            selectedExamType !== "Random Exams" &&
            selectedExamType !== "Other Exams"
          ) {
            examNames = ["Unnamed"];
          }

          setAvailableExamNames(examNames);
          setSelectedExamName(examNames.length > 0 ? examNames[0] : "");
        } catch (err) {
          console.error("Error fetching exam names:", err);
          setAvailableExamNames([]);
          setSelectedExamName("");
        } finally {
          setFetchingExamNames(false);
        }
      };

      fetchExamNames();
    } else {
      setAvailableExamNames([]);
      setSelectedExamName("");
    }
  }, [selectedExamType, selectedYear, selectedClass, selectedTerm, schoolId]);

  

  // Fetch performance data - UPDATED VERSION
  // Fetch performance data - FIXED LOGIC
  const fetchPerformanceData = async () => {
    if (!selectedYear || !selectedClass || !selectedTerm || !selectedExamType) {
      setError("Please select all required criteria");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPerformanceData({});

      const params = {
        class: selectedClass,
        term: selectedTerm,
        examType: selectedExamType,
        academicYear: selectedYear,
      };

      // Handle examName logic properly
      if (selectedExamName && selectedExamName.trim() !== "") {
        // If it's "Unnamed", send empty string to fetch exams with no name
        if (selectedExamName === "Unnamed") {
          params.examName = "";
        } else {
          // Otherwise, send the actual exam name
          params.examName = selectedExamName;
        }
      }

      console.log("Fetching with params:", params);

      const response = await axios.get(
        `https://ed-9ny1.onrender.com/api/schools/${schoolId}/performance/filtered-by-year`, // NEW
        { params, timeout: 90000 }
      );

      console.log("Performance data received:", response.data);

      if (!response.data || Object.keys(response.data).length === 0) {
        setError("No data found for the selected criteria");
        setPerformanceData({});
      } else {
        setPerformanceData(response.data);
      }
    } catch (err) {
      console.error("Error fetching performance data:", err);
      if (err.code === "ECONNABORTED") {
        setError("Request timeout. Please try again.");
      } else {
        setError(
          err.response?.data?.message || "Failed to load performance data"
        );
      }
      setPerformanceData({});
    } finally {
      setLoading(false);
    }
  };

  // Fetch term performance
  const fetchTermPerformance = async (grade, term) => {
    const termKey = `${grade}-${term}-${selectedYear}`;

    if (termPerformanceData[termKey]) return;

    try {
      setTermLoading((prev) => ({ ...prev, [termKey]: true }));

      const response = await axios.get(
        `https://ed-9ny1.onrender.com/api/schools/${schoolId}/performance/term`,
        {
          params: {
            class: grade,
            term,
            academicYear: selectedYear,
          },
          timeout: 30000,
        }
      );

      console.log("Term performance received:", response.data);

      const processedData = {
        ...response.data,
        subjects: Array.isArray(response.data.subjects)
          ? response.data.subjects
          : Array.from(response.data.subjects || []),
      };

      setTermPerformanceData((prev) => ({
        ...prev,
        [termKey]: processedData,
      }));
    } catch (err) {
      console.error("Error fetching term performance:", err);
      setError("Failed to load term performance data");
    } finally {
      setTermLoading((prev) => ({ ...prev, [termKey]: false }));
    }
  };

  const handleGradeChange = (grade) => {
    setExpandedGrade(expandedGrade === grade ? null : grade);
    setExpandedTerm(null);
  };

  const handleTermChange = (term, grade) => {
    setExpandedTerm(expandedTerm === term ? null : term);

    if (expandedTerm !== term) {
      fetchTermPerformance(grade, term);
    }
  };

  const resetSelections = () => {
    setSelectedYear(availableYears[0] || "");
    setSelectedClass("");
    setSelectedTerm("");
    setSelectedExamType("");
    setSelectedExamName("");
    setPerformanceData({});
    setTermPerformanceData({});
    setError(null);
    fetchClasses(selectedYear);
  };

  if (!schoolId) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          py: 4,
          bgcolor: "#f8f9fa",
          minHeight: "100vh",
          textAlign: "center",
        }}
      >
        <Typography sx={{ color: "red", mb: 2 }}>
          School information not available
        </Typography>
        <Button
          variant="contained"
          sx={{ bgcolor: "#6200ee" }}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ py: 4, bgcolor: "#f8f9fa", minHeight: "100vh" }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "#2c3e50", mb: 3 }}
        >
          School Performance
        </Typography>

        {/* Selection Form */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Select Performance Criteria
          </Typography>

          <Grid container spacing={2}>
            {/* Academic Year */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Academic Year"
                  disabled={initialLoad}
                >
                  <MenuItem value="">Select Year</MenuItem>
                  {availableYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Class */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth disabled={!selectedYear}>
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  label="Class"
                >
                  <MenuItem value="">Select Class</MenuItem>
                  {availableClasses.map((cls) => (
                    <MenuItem key={cls} value={cls}>
                      {cls}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Term */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth disabled={!selectedClass}>
                <InputLabel>Term</InputLabel>
                <Select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  label="Term"
                >
                  <MenuItem value="">Select Term</MenuItem>
                  {availableTerms.map((term) => (
                    <MenuItem key={term} value={term}>
                      {term}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Exam Type */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={!selectedTerm}>
                <InputLabel>Exam Type</InputLabel>
                <Select
                  value={selectedExamType}
                  onChange={(e) => setSelectedExamType(e.target.value)}
                  label="Exam Type"
                >
                  <MenuItem value="">Select Exam Type</MenuItem>
                  {availableExamTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            

            {/* Exam Name (show for ALL exam types now) */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl
                fullWidth
                disabled={!selectedExamType || fetchingExamNames}
              >
                <InputLabel>Exam Name</InputLabel>
                <Select
                  value={selectedExamName}
                  onChange={(e) => setSelectedExamName(e.target.value)}
                  label="Exam Name"
                >
                  <MenuItem value="">Select Exam Name</MenuItem>
                  {fetchingExamNames ? (
                    <MenuItem value="" disabled>
                      <CircularProgress size={20} />
                      Loading...
                    </MenuItem>
                  ) : (
                    availableExamNames.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {selectedExamType &&
                  !fetchingExamNames &&
                  availableExamNames.length === 0 && (
                    <FormHelperText>
                      No specific exam names found
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              onClick={fetchPerformanceData}
              disabled={
                loading ||
                !selectedYear ||
                !selectedClass ||
                !selectedTerm ||
                !selectedExamType ||
                ((selectedExamType === "Random Exams" ||
                  selectedExamType === "Other Exams") &&
                  !selectedExamName)
              }
              sx={{ minWidth: 200 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Load Performance Data"
              )}
            </Button>

            <Button variant="outlined" onClick={resetSelections}>
              Reset
            </Button>

            <Typography variant="body2" sx={{ ml: 2, color: "text.secondary" }}>
              Selected: {selectedYear}{" "}
              {selectedClass ? `| ${selectedClass}` : ""}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading performance data...</Typography>
          </Box>
        )}

        {/* Results Display */}
        {!loading && Object.keys(performanceData).length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, color: "#6200ee" }}>
              Performance Results
            </Typography>

            {Object.keys(performanceData).map((grade) => (
              <Accordion
                key={grade}
                expanded={expandedGrade === grade}
                onChange={() => handleGradeChange(grade)}
                sx={{ mb: 2, boxShadow: 3 }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ fontWeight: "bold" }}>{grade}</Typography>
                    <Chip label={`Year: ${selectedYear}`} size="small" />
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  {Object.keys(performanceData[grade]).map((term) => (
                    <Accordion
                      key={term}
                      expanded={expandedTerm === term}
                      onChange={() => handleTermChange(term, grade)}
                      sx={{ mb: 2 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography sx={{ fontWeight: "600" }}>
                          {term}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails>
                        {/* Term Summary Card */}
                        <Card
                          sx={{
                            mb: 3,
                            border: "2px solid #6200ee",
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#f5f5f5" },
                          }}
                          onClick={() => {
                            const termKey = `${grade}-${term}-${selectedYear}`;
                            const termData = termPerformanceData[termKey];

                            if (termData) {
                              navigate("/performance-analysis", {
                                state: {
                                  grade,
                                  term,
                                  academicYear: selectedYear,
                                  examType: "Term Summary",
                                  examName: `All Exams in ${term}`,
                                  data: termData,
                                  schoolId,
                                  isTermSummary: true,
                                },
                              });
                            }
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                sx={{ fontWeight: "bold", color: "#6200ee" }}
                              >
                                Term Performance Summary
                              </Typography>
                              {termLoading[
                                `${grade}-${term}-${selectedYear}`
                              ] && <CircularProgress size={20} />}
                            </Box>

                            {termPerformanceData[
                              `${grade}-${term}-${selectedYear}`
                            ] ? (
                              <>
                                <Typography sx={{ color: "#666", mb: 1 }}>
                                  {
                                    termPerformanceData[
                                      `${grade}-${term}-${selectedYear}`
                                    ].learners.length
                                  }{" "}
                                  students
                                </Typography>
                                <Typography sx={{ color: "#666", mb: 1 }}>
                                  Average:{" "}
                                  {
                                    termPerformanceData[
                                      `${grade}-${term}-${selectedYear}`
                                    ].overallAverage
                                  }
                                  %
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mt: 1,
                                  }}
                                >
                                  {termPerformanceData[
                                    `${grade}-${term}-${selectedYear}`
                                  ].subjects?.map((subject) => (
                                    <Chip
                                      key={subject}
                                      label={subject}
                                      size="small"
                                      sx={{ bgcolor: "#e0e0e0" }}
                                    />
                                  ))}
                                </Box>
                              </>
                            ) : (
                              <Typography
                                sx={{ color: "#666", fontStyle: "italic" }}
                              >
                                Click to load term summary
                              </Typography>
                            )}
                          </CardContent>
                        </Card>

                        {/* Individual Exams */}
                        {Object.keys(performanceData[grade][term]).map(
                          (examKey) => {
                            const examData =
                              performanceData[grade][term][examKey];
                            return (
                              <Card
                                key={examKey}
                                sx={{
                                  mb: 2,
                                  border: "1px solid #ddd",
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: "#f9f9f9" },
                                }}
                                onClick={() =>
                                  navigate("/performance-analysis", {
                                    state: {
                                      grade,
                                      term,
                                      academicYear: selectedYear,
                                      examType: examData.baseExamType,
                                      examName: examData.examName,
                                      data: examData,
                                      schoolId,
                                    },
                                  })
                                }
                              >
                                <CardContent>
                                  <Typography
                                    sx={{ fontWeight: "bold", mb: 1 }}
                                  >
                                    {examKey}
                                  </Typography>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {examData.learners.length} students
                                    </Typography>
                                    <Chip
                                      label={`${selectedYear}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>

                                  {examData.subjects &&
                                  examData.subjects.length > 0 ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                      }}
                                    >
                                      {examData.subjects.map((subject) => (
                                        <Chip
                                          key={subject}
                                          label={subject}
                                          size="small"
                                          sx={{
                                            bgcolor: "#f0f0f0",
                                            fontSize: "0.7rem",
                                            height: "24px",
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      fontStyle="italic"
                                    >
                                      No subjects found
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            );
                          }
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}

        {/* No Data Message */}
        {!loading &&
          Object.keys(performanceData).length === 0 &&
          selectedClass && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No performance data found for the selected criteria. Please adjust
              your selections and try again.
            </Alert>
          )}

        {/* Initial Load Message */}
        {initialLoad && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading academic years...</Typography>
          </Box>
        )}
      </motion.div>
    </Container>
  );
};

export default SchoolPerformance;
